import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type ReactElement,
} from 'react';
import { Info } from 'lucide-react';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';
import { diffRowAgainstBaseline } from '../lib/rowDiff';
import {
  addRowToList,
  removeRowById,
  replaceRowInList,
} from '../lib/listHelpers';
import {
  evaluateEditableTableValid,
  isDraftRowEmpty,
  isDraftRowValid,
} from '../lib/tableValidation';
import type {
  EditableColumnDef,
  EditableRow,
  ValidateCellFn,
  ValidateCellInput,
} from '../types';
import { uuidV4 } from '@/lib/uuid';
import { EditableRowTableRow } from './EditableRowTableRow';

export interface EditableRowTableProps {
  columns: EditableColumnDef[];
  rows: EditableRow[];
  baselineRows: EditableRow[];
  onRowsChange: (next: EditableRow[]) => void;
  readOnly: boolean;
  /**
   * Support role: committed rows stay viewable and can be expanded into the
   * edit form, but their commit actions (row Save, Remove, Restore, Revert)
   * are disabled. The add-row draft stays fully locked regardless — support
   * never gets to create new rows. Ignored when `readOnly` is false.
   */
  isSupportReadOnly?: boolean;
  createEmptyRow: () => EditableRow;
  /** Per-cell validation; return a message to show error border + badge. */
  validateCell?: ValidateCellFn;
  /**
   * Fires when **committed** row validity changes. The add-row draft is excluded so
   * invalid draft input does not block saving committed rows (parent state has no draft).
   */
  onValidationChange?: (valid: boolean) => void;
  /**
   * Override for “empty draft” (e.g. MCP `transport` default `any` is not user content).
   * Defaults to all field values blank after trim.
   */
  isDraftEmpty?: (row: EditableRow) => boolean;
}

export function EditableRowTable({
  columns,
  rows,
  baselineRows,
  onRowsChange,
  readOnly,
  isSupportReadOnly = false,
  createEmptyRow,
  validateCell,
  onValidationChange,
  isDraftEmpty: isDraftEmptyProp,
}: EditableRowTableProps): ReactElement {
  const isDraftEmptyFn = isDraftEmptyProp ?? isDraftRowEmpty;
  /** True lock: not relaxed for support. Draft/deleted rows always use this. */
  const isFullyLocked = readOnly && !isSupportReadOnly;
  const fieldIds = useMemo(() => columns.map(c => c.id), [columns]);

  const rowGridStyle = useMemo(
    (): CSSProperties => ({
      gridTemplateColumns: `repeat(${columns.length}, minmax(0, 1fr)) minmax(4.5rem, auto)`,
    }),
    [columns.length]
  );

  const baselineById = useMemo(() => {
    const map = new Map<string, EditableRow>();
    for (const b of baselineRows) {
      map.set(b.id, b);
    }
    return map;
  }, [baselineRows]);

  const [draft, setDraft] = useState<EditableRow>(() => createEmptyRow());

  /** Single expanded committed row — edit buffer until Save. */
  const [editingRowId, setEditingRowId] = useState<string | null>(null);
  const [editBuffer, setEditBuffer] = useState<EditableRow | null>(null);
  const [expandedSaveAttempted, setExpandedSaveAttempted] = useState(false);

  const editingAnotherRow = editingRowId != null;
  const tableRootRef = useRef<HTMLDivElement>(null);

  const cancelRowEdit = useCallback((): void => {
    setEditingRowId(null);
    setEditBuffer(null);
    setExpandedSaveAttempted(false);
  }, []);

  const beginRowEdit = useCallback(
    (row: EditableRow): void => {
      if (isFullyLocked) {
        return;
      }
      setExpandedSaveAttempted(false);
      setEditingRowId(row.id);
      setEditBuffer({
        id: row.id,
        fields: { ...row.fields },
        ...(row.pendingDelete !== undefined && {
          pendingDelete: row.pendingDelete,
        }),
      });
    },
    [isFullyLocked]
  );

  useEffect(() => {
    if (!editingRowId) {
      return;
    }
    const el = tableRootRef.current;
    if (!el) {
      return;
    }
    const onKey = (e: KeyboardEvent): void => {
      if (e.key !== 'Escape') {
        return;
      }
      e.preventDefault();
      e.stopPropagation();
      cancelRowEdit();
    };
    el.addEventListener('keydown', onKey, true);
    return () => {
      el.removeEventListener('keydown', onKey, true);
    };
  }, [editingRowId, cancelRowEdit]);

  /** Include in-flight expanded edit so Save / policy validity match the form. */
  const rowsForValidation = useMemo(() => {
    if (!editingRowId || !editBuffer) {
      return rows;
    }
    return replaceRowInList(rows, editingRowId, editBuffer);
  }, [rows, editingRowId, editBuffer]);

  /** So validators (e.g. duplicate detection) see the same committed rows as table validity. */
  const validateCellWithPeers = useMemo((): ValidateCellFn | undefined => {
    if (!validateCell) {
      return undefined;
    }
    return (input: ValidateCellInput): string | undefined =>
      validateCell({
        ...input,
        // Respect explicit peerRows when provided (e.g. draft validation).
        peerRows: input.peerRows ?? rowsForValidation,
      });
  }, [validateCell, rowsForValidation]);

  /**
   * Committed rows only — draft row is UI-local until "+" and must not block auto-save
   * of committed rules or dirty/baseline sync for the parent form.
   */
  const tableValid = useMemo(
    () =>
      evaluateEditableTableValid(
        rowsForValidation,
        draft,
        columns,
        validateCellWithPeers,
        isDraftEmptyFn,
        true
      ),
    // `draft` is listed so deps match the callback body; result is unchanged when draft
    // moves because skipDraftRowValidation is always true (draft not used for this flag).
    [rowsForValidation, draft, columns, validateCellWithPeers, isDraftEmptyFn]
  );

  const draftHasContent = useMemo(
    () => !isDraftEmptyFn(draft),
    [draft, isDraftEmptyFn]
  );

  const draftPeerRows = useMemo(
    () => [...rowsForValidation, draft],
    [rowsForValidation, draft]
  );

  const draftRowValid = useMemo(
    () =>
      isDraftRowValid(
        draft,
        columns,
        validateCellWithPeers,
        isDraftEmptyFn,
        draftPeerRows
      ),
    [draft, columns, validateCellWithPeers, isDraftEmptyFn, draftPeerRows]
  );

  useEffect(() => {
    onValidationChange?.(tableValid);
  }, [tableValid, onValidationChange]);

  const handleDraftChange = (next: EditableRow): void => {
    setDraft(next);
  };

  const handleAddDraft = (): void => {
    if (!draftHasContent || !draftRowValid) {
      return;
    }
    onRowsChange(addRowToList(rows, { ...draft, id: uuidV4() }));
    setDraft(createEmptyRow());
  };

  const saveRowEdit = useCallback((): void => {
    if (!editBuffer || !editingRowId) {
      return;
    }
    if (validateCellWithPeers) {
      for (const col of columns) {
        const msg = validateCellWithPeers({
          row: editBuffer,
          column: col,
          value: editBuffer.fields[col.id] ?? '',
        });
        if (msg) {
          setExpandedSaveAttempted(true);
          return;
        }
      }
    }
    onRowsChange(replaceRowInList(rows, editingRowId, editBuffer));
    cancelRowEdit();
  }, [
    cancelRowEdit,
    columns,
    editBuffer,
    editingRowId,
    onRowsChange,
    rows,
    validateCellWithPeers,
  ]);

  const handleRemove = (id: string): void => {
    const row = rows.find(r => r.id === id);
    if (!row) {
      return;
    }
    const inBaseline = baselineRows.some(b => b.id === id);
    if (inBaseline) {
      onRowsChange(replaceRowInList(rows, id, { ...row, pendingDelete: true }));
    } else {
      onRowsChange(removeRowById(rows, id));
    }
  };

  const handleRestore = (id: string): void => {
    const row = rows.find(r => r.id === id);
    if (!row) {
      return;
    }
    /** Omit `pendingDelete` so row shape matches baseline (`JSON.stringify` omits absent keys). */
    onRowsChange(
      replaceRowInList(rows, id, {
        id: row.id,
        fields: { ...row.fields },
      })
    );
  };

  const handleRevertToBaseline = (id: string): void => {
    const base = baselineRows.find(b => b.id === id);
    if (!base) {
      return;
    }
    onRowsChange(
      replaceRowInList(rows, id, {
        id: base.id,
        fields: { ...base.fields },
      })
    );
  };

  return (
    <TooltipProvider delayDuration={400}>
      <div
        ref={tableRootRef}
        data-editable-row-table
        role="table"
        aria-label="Allow and block rules"
        className="flex flex-col max-h-[min(22rem,45vh)] rounded-md border bg-muted/20 dark:bg-background/55 overflow-x-hidden overflow-y-hidden"
      >
        <div
          role="row"
          style={rowGridStyle}
          className={cn(
            'grid shrink-0 gap-x-2 gap-y-1 items-end px-2 pt-2 pb-1 border-b bg-card dark:bg-surface-elevated-muted transition-opacity duration-200',
            editingAnotherRow &&
              'opacity-[0.38] transition-opacity duration-200'
          )}
        >
          {columns.map(col => (
            <div
              key={col.id}
              role="columnheader"
              className="min-w-0 flex items-end gap-1 text-xs font-medium text-muted-foreground dark:text-foreground/85"
            >
              <span className="min-w-0 truncate">{col.headerLabel}</span>
              {col.headerTooltip ? (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <button
                      type="button"
                      className="shrink-0 text-muted-foreground hover:text-foreground rounded-sm"
                      aria-label={`${col.headerLabel} column help`}
                    >
                      <Info className="size-3.5" />
                    </button>
                  </TooltipTrigger>
                  <TooltipContent
                    sideOffset={6}
                    className="max-w-sm whitespace-normal leading-snug antialiased"
                  >
                    {col.headerTooltip}
                  </TooltipContent>
                </Tooltip>
              ) : null}
            </div>
          ))}
          <div className="min-w-0" aria-hidden />
        </div>
        <div
          role="rowgroup"
          className="flex-1 min-h-0 overflow-y-auto overflow-x-hidden overscroll-contain px-2 py-1.5 space-y-1.5"
        >
          {rows.length === 0 ? (
            <div className="my-3 rounded-md border border-dashed border-border/60 bg-card/40 px-3 py-2 text-center text-xs text-muted-foreground">
              {readOnly
                ? 'No entries available.'
                : 'No entries yet. Add your first one below.'}
            </div>
          ) : null}
          {rows.map(row => {
            if (row.pendingDelete) {
              return (
                <EditableRowTableRow
                  key={row.id}
                  columns={columns}
                  rowGridStyle={rowGridStyle}
                  row={row}
                  onChange={() => {}}
                  readOnly={readOnly}
                  variant="deleted"
                  onRestore={() => handleRestore(row.id)}
                  validateCell={validateCellWithPeers}
                  dimmed={editingAnotherRow}
                />
              );
            }
            const rowForDiff =
              editingRowId === row.id && editBuffer ? editBuffer : row;
            const d = diffRowAgainstBaseline(
              rowForDiff,
              baselineById.get(row.id),
              fieldIds
            );
            const showRevertInsteadOfRemove =
              d.kind === 'modified' && !isFullyLocked;
            const isEditing = editingRowId === row.id && editBuffer != null;
            return (
              <EditableRowTableRow
                key={row.id}
                columns={columns}
                rowGridStyle={rowGridStyle}
                row={isEditing ? editBuffer : row}
                onChange={isEditing ? next => setEditBuffer(next) : undefined}
                readOnly={isFullyLocked}
                commitDisabled={isSupportReadOnly}
                variant="committed"
                committedLayout={isEditing ? 'expanded' : 'collapsed'}
                onBeginEdit={() => beginRowEdit(row)}
                onSaveEdit={saveRowEdit}
                onCancelEdit={cancelRowEdit}
                expandedShowValidation={expandedSaveAttempted}
                dimmed={editingAnotherRow && !isEditing}
                onRemove={() => {
                  if (editingRowId === row.id) {
                    cancelRowEdit();
                  }
                  handleRemove(row.id);
                }}
                onRevertToBaseline={
                  showRevertInsteadOfRemove
                    ? () => {
                        if (editingRowId === row.id) {
                          cancelRowEdit();
                        }
                        handleRevertToBaseline(row.id);
                      }
                    : undefined
                }
                rowDiff={d.kind}
                modifiedFieldIds={d.modifiedFieldIds}
                validateCell={validateCellWithPeers}
              />
            );
          })}
        </div>
        <div
          className={cn(
            'shrink-0 border-t border-border bg-muted/75 dark:bg-surface-elevated-muted px-2 py-1.5 transition-opacity duration-200',
            editingAnotherRow &&
              'opacity-[0.38] transition-opacity duration-200'
          )}
        >
          <EditableRowTableRow
            columns={columns}
            rowGridStyle={rowGridStyle}
            row={draft}
            onChange={handleDraftChange}
            readOnly={readOnly}
            variant="draft"
            onAdd={handleAddDraft}
            validateCell={validateCellWithPeers}
            showValidationErrors={draftHasContent && !editingAnotherRow}
            addDisabled={!draftHasContent || !draftRowValid}
          />
        </div>
      </div>
    </TooltipProvider>
  );
}
